/**
 * What the payables queue concluded about one invoice, in a sentence.
 *
 * A sentence, not a badge, because "matched" and "order matched, not paid
 * yet" are different claims and only the second tells a reviewer what to do
 * next. A green tick alone would be a liability: the person clicking Approve
 * is accountable for the payment, so the queue says what it compared and the
 * evidence behind the sentence is one click away.
 *
 * `needsPerson` is not "low confidence". It is the narrower claim that this
 * invoice cannot be approved without somebody deciding something: an order to
 * pick, a shortfall to accept, a missing order to explain. It drives both the
 * amber text and the absence of the Approve button, so the two can never
 * disagree.
 *
 * Pure: reads only the payable, never a clock or a locale.
 */

import type { ApiPayable } from "../api/schemas/payables.js"

export type PayableVerdict = {
  readonly text: string
  /** True when a person has to decide before this can be approved. */
  readonly needsPerson: boolean
}

/** Cents to a plain "$1,250.00", for the one place a verdict names money. */
const money = (cents: number): string =>
  `$${(Math.abs(cents) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

/**
 * The shortfall between an invoice total and the payment matched to it, or
 * null when either side is unknown. Absolute: "short" and "over" are
 * different sentences, and the number in each is unsigned.
 */
const paymentGapCents = (payable: ApiPayable): number | null => {
  const matched = payable.payment.match
  if (!matched || payable.totalCents === null) return null
  const evidence = matched.evidence.find(
    (entry) => entry.check === "payment_amount"
  )
  if (!evidence || evidence.outcome !== "failed") return null
  const actual = Number(evidence.actual?.replace(/[^0-9.-]/g, ""))
  if (!Number.isFinite(actual)) return null
  return Math.round(actual * 100) - payable.totalCents
}

export const payableVerdict = (payable: ApiPayable): PayableVerdict => {
  const po = payable.po.kind
  const payment = payable.payment.kind

  // Ambiguity first, because it is the one state where the machine found
  // several good answers and refuses to pick. Guessing here is the same
  // mistake the transfer pairer declines to make.
  if (po === "ambiguous") {
    return {
      text: `${payable.po.candidates.length} orders match, pick one`,
      needsPerson: true,
    }
  }
  if (payment === "ambiguous") {
    return {
      text: `${payable.payment.candidates.length} payments match, pick one`,
      needsPerson: true,
    }
  }

  if (payment === "near_miss") {
    const gap = paymentGapCents(payable)
    if (gap !== null && gap !== 0) {
      return {
        text: `Payment is ${money(gap)} ${gap < 0 ? "short" : "over"}`,
        needsPerson: true,
      }
    }
    return { text: "A payment is close but does not agree", needsPerson: true }
  }

  if (payment === "refund_suspected") {
    return { text: "A credit matches this total, not a payment", needsPerson: true }
  }

  if (po === "none") {
    return { text: "No order found", needsPerson: true }
  }

  // Everything below is clean. `skipped` means the engagement turned that
  // side of the matching off, so its silence is a setting rather than a gap
  // and must not read as a warning.
  const orderPart =
    po === "matched" ? "Order" : po === "skipped" ? null : "Order"

  if (payment === "matched") {
    return {
      text: orderPart ? "Order and payment matched" : "Payment matched",
      needsPerson: false,
    }
  }
  if (payment === "skipped") {
    return {
      text: orderPart ? "Order matched" : "Nothing left to match",
      needsPerson: false,
    }
  }
  // `none` is unpaid, which is pending and not an exception.
  return {
    text: orderPart ? "Order matched, not paid yet" : "Not paid yet",
    needsPerson: false,
  }
}

/**
 * Whether the queue's Approve button belongs on this row.
 *
 * Absent rather than disabled when it does not, so the column reads as a
 * to-do list at a glance. An invoice held back by open required review flags
 * is not approvable from here either: the flags are the decision, and the
 * document's own review screen is where they get resolved.
 */
export const payableIsApprovable = (payable: ApiPayable): boolean =>
  !payable.approved &&
  !payable.blockedByFlags &&
  !payableVerdict(payable).needsPerson
